/**
 * localStorage/sessionStorage can be missing or throw (private mode, blocked site
 * data, quota). Every access goes through these helpers and degrades to memory.
 */
const memory = new Map<string, string>();

function store(kind: 'local' | 'session'): Storage | null {
  try {
    const s = kind === 'local' ? window.localStorage : window.sessionStorage;
    const probe = '__plansgx_probe__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

export function readJson<T>(key: string, fallback: T, kind: 'local' | 'session' = 'local'): T {
  try {
    const raw = store(kind)?.getItem(key) ?? memory.get(`${kind}:${key}`) ?? null;
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function writeJson(key: string, value: unknown, kind: 'local' | 'session' = 'local') {
  const raw = JSON.stringify(value);
  memory.set(`${kind}:${key}`, raw);
  try {
    store(kind)?.setItem(key, raw);
  } catch {
    // quota or blocked storage: memory copy is enough for this session
  }
}

export function removeKey(key: string, kind: 'local' | 'session' = 'local') {
  memory.delete(`${kind}:${key}`);
  try {
    store(kind)?.removeItem(key);
  } catch {
    // ignore
  }
}

/** Removes every key with a prefix (used on owner sign-out). */
export function removePrefix(prefix: string) {
  for (const key of [...memory.keys()]) if (key.split(':').slice(1).join(':').startsWith(prefix)) memory.delete(key);
  for (const kind of ['local', 'session'] as const) {
    const s = store(kind);
    if (!s) continue;
    try {
      for (let i = s.length - 1; i >= 0; i -= 1) {
        const key = s.key(i);
        if (key?.startsWith(prefix)) s.removeItem(key);
      }
    } catch {
      // ignore
    }
  }
}
